<script setup>
import { ref, computed, getCurrentInstance, onMounted } from 'vue';
import { router } from '@inertiajs/vue3';
import { useLoading } from '@/composables/useLoading';
import { useReviewHistory } from '@/composables/useReviewHistory';
import { useToast } from 'primevue/usetoast';
import RecipientContactBlock from '@/components/admin/orders/RecipientContactBlock.vue';
import axios from 'axios';
import SiteFeaturesInfo from '@/components/SiteFeaturesInfo.vue';
import SendForApprovalModal from '@/components/admin/orders/SendForApprovalModal.vue';
import ClientEmailMessagePanel from '@/components/admin/ClientEmailMessagePanel.vue';
import AdminForceApproveModal from '@/components/admin/orders/AdminForceApproveModal.vue';
import ReviewHistoryDialog from '@/components/shared/ReviewHistoryDialog.vue';

defineOptions({
    inheritAttrs: false
});

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const { showLoading, hideLoading } = useLoading();
const toast = useToast();

const props = defineProps({
    order: {
        type: Object,
        required: true
    },
    itemsWithContent: {
        type: Array,
        default: () => []
    },
    allConfirmed: {
        type: Boolean,
        default: false
    },
    hasContent: {
        type: Boolean,
        default: false
    },
    isEnabled: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['content-confirmed', 'refresh-needed']);

// Content Review variables
const adminLinkInput = ref({});
const showAdminLinkModal = ref(false);
const selectedContentId = ref(null);
const showValidationErrorModal = ref(false);
const validationErrorMessage = ref('');
const validationWordCount = ref(0);
const showSimilarityConfirmation = ref(false);
const similarityData = ref(null);
const showRejectModal = ref(false);
const selectedRejectContentId = ref(null);
const rejectReason = ref('');
const rejectEmailOptions = ref({ message: null, locale: null });
const rejectingContent = ref(false);
const showClearLinkModal = ref(false);
const selectedClearContentId = ref(null);
const addingLink = ref(false);
const showSendToClientApprovalModal = ref(false);
const selectedSendApprovalContentId = ref(null);
const sendingToClientApproval = ref(false);
const showForceApproveModal = ref(false);
const selectedForceApproveContentId = ref(null);
const expandedItems = ref({});

const { showHistoryDialog, historyItem, openHistoryDialog, closeHistoryDialog, actionLabel, actionSeverityClass, actionIcon } = useReviewHistory();

const EXPANDED_KEY = `content_review_expanded_${props.order.id}`;

onMounted(() => {
    const saved = localStorage.getItem(EXPANDED_KEY);
    if (saved) {
        try {
            expandedItems.value = JSON.parse(saved);
        } catch (e) {
            console.warn('Failed to load expanded state:', e);
        }
    }
});

const confirmContent = async (contentId) => {
    selectedContentId.value = contentId;
    
    // Get the content item from itemsWithContent
    const contentItem = props.itemsWithContent.find(item => item.content && item.content.id === contentId);
    
    if (contentItem && contentItem.content) {
        // Validate that content has required links
        if (!isContentValid(contentItem.content)) {
            toast.add({
                severity: 'error',
                summary: $t('Content Invalid'),
                detail: contentItem.content.auto_content === 1 
                    ? $t('Google Docs link is required for suggested articles')
                    : $t('Content link or file is required'),
                life: 5000
            });
            return;
        }
        
        // Validate Google Docs if applicable
        if (contentItem.content.content_link && contentItem.content.auto_content === 0) {
            try {
                showLoading($t('Validating Google Docs content...'));
                
                const response = await axios.post('/google-docs/validate-content', {
                    google_docs_url: contentItem.content.content_link,
                    anchor_name: contentItem.content.anchor_name?.trim() || null,
                    anchor_url: contentItem.content.anchor_url?.trim() || null,
                    anchor_name_2: contentItem.content.anchor_name_2?.trim() || null,
                    anchor_url_2: contentItem.content.anchor_url_2?.trim() || null,
                    anchor_name_3: contentItem.content.anchor_name_3?.trim() || null,
                    anchor_url_3: contentItem.content.anchor_url_3?.trim() || null,
                });
                
                if (response.data.valid === 'similar') {
                    hideLoading();
                    
                    similarityData.value = {
                        similarity: response.data.similarity,
                        expected: response.data.expected,
                        found: response.data.found,
                        url: response.data.url,
                        position: response.data.position || 1
                    };
                    showSimilarityConfirmation.value = true;
                    return;
                } else if (response.data.valid === false) {
                    hideLoading();
                    
                    let errorDetail = '';
                    if (response.data.errors && Array.isArray(response.data.errors)) {
                        errorDetail = response.data.errors.join('\n');
                    } else {
                        errorDetail = response.data.error || $t('Google Docs validation failed');
                    }
                    
                    toast.add({
                        severity: 'error',
                        summary: $t('Validation Error'),
                        detail: errorDetail,
                        life: 8000
                    });
                    return;
                }
            } catch (error) {
                hideLoading();
                
                if (error.response?.data?.valid === false) {
                    let errorDetail = '';
                    if (error.response.data.errors && Array.isArray(error.response.data.errors)) {
                        errorDetail = error.response.data.errors.join('\n');
                    } else {
                        errorDetail = error.response.data.error || $t('Google Docs validation failed');
                    }
                    
                    toast.add({
                        severity: 'error',
                        summary: $t('Validation Error'),
                        detail: errorDetail,
                        life: 8000
                    });
                    return;
                } else {
                    toast.add({
                        severity: 'warn',
                        summary: $t('Validation Warning'),
                        detail: $t('Could not validate Google Docs content. Proceeding with confirmation.'),
                        life: 3000
                    });
                }
            }
        }
    }
    
    proceedWithConfirmation(contentId);
};

const openAdminLinkModal = (contentId) => {
    selectedContentId.value = contentId;
    const item = props.itemsWithContent.find(i => i.content?.id === contentId);
    adminLinkInput.value[contentId] = item?.content?.content_link || '';
    showAdminLinkModal.value = true;
};

const closeAdminLinkModal = () => {
    showAdminLinkModal.value = false;
    selectedContentId.value = null;
    adminLinkInput.value = {};
    addingLink.value = false;
};

const openValidationErrorModal = (errorMessage, wordCount = 0) => {
    validationErrorMessage.value = errorMessage;
    validationWordCount.value = wordCount;
    showValidationErrorModal.value = true;
};

const closeValidationErrorModal = () => {
    showValidationErrorModal.value = false;
    validationErrorMessage.value = '';
    validationWordCount.value = 0;
};

const confirmSimilarity = () => {
    showSimilarityConfirmation.value = false;
    const contentId = selectedContentId.value;
    const link = adminLinkInput.value[contentId];

    if (showAdminLinkModal.value || link) {
        similarityData.value = null;
        proceedWithAddingLink(contentId, link?.trim());
    } else {
        similarityData.value = null;
        proceedWithConfirmation(contentId);
    }
};

const cancelSimilarity = () => {
    showSimilarityConfirmation.value = false;
    similarityData.value = null;
};

const highlightDifferences = (text1, text2) => {
    const words1 = text1.split(' ');
    const words2 = text2.split(' ');
    const maxLength = Math.max(words1.length, words2.length);
    
    let highlighted1 = '';
    let highlighted2 = '';
    
    for (let i = 0; i < maxLength; i++) {
        const word1 = words1[i] || '';
        const word2 = words2[i] || '';
        
        if (word1 !== word2) {
            highlighted1 += `<span class="bg-red-200 text-red-800 px-1 rounded">${word1}</span> `;
            highlighted2 += `<span class="bg-green-200 text-green-800 px-1 rounded">${word2}</span> `;
        } else {
            highlighted1 += word1 + ' ';
            highlighted2 += word2 + ' ';
        }
    }
    
    return {
        highlighted1: highlighted1.trim(),
        highlighted2: highlighted2.trim()
    };
};

const proceedWithConfirmation = (contentId, message = null) => {
    router.post(
        proxy.route ? proxy.route('order-content.confirm', contentId) : `/admin/order-content/${contentId}/confirm`,
        { message },
        {
            preserveScroll: true,
            preserveState: true,
            onStart: () => showLoading($t('Confirming content...')),
            onFinish: () => hideLoading(),
            onSuccess: () => {
                // Clear expanded state when content is confirmed - task is complete
                localStorage.removeItem(EXPANDED_KEY);
                expandedItems.value = {};
                
                emit('refresh-needed');
                emit('content-confirmed');
            },
            onError: (errors) => {
                hideLoading();
                if (errors.validation) {
                    const wordCountMatch = errors.validation.match(/(\d+)\s+words/);
                    if (wordCountMatch) {
                        const wordCount = parseInt(wordCountMatch[1]);
                        openValidationErrorModal(errors.validation, wordCount);
                        return;
                    }
                    
                    const anchorErrorCode = proxy.$page?.props?.flash?.anchor_error_code;
                    if (anchorErrorCode) {
                        const friendlyMessage = translateAnchorError(anchorErrorCode);
                        openValidationErrorModal(friendlyMessage, 0);
                        return;
                    }
                    
                    openValidationErrorModal(errors.validation, 0);
                }
            },
        }
    );
};

const submitAdminLink = async () => {
    const contentId = selectedContentId.value;
    const link = adminLinkInput.value[contentId];
    
    if (!link || !link.trim()) {
        alert($t('Please enter a valid Google Docs link'));
        return;
    }
    
    addingLink.value = true;
    const contentItem = props.itemsWithContent.find(item => item.content && item.content.id === contentId);
    
    if (contentItem && contentItem.content) {
        try {
            showLoading($t('Validating Google Docs content...'));
            
            const response = await axios.post('/google-docs/validate-content', {
                google_docs_url: link.trim(),
                anchor_name: contentItem.content.anchor_name?.trim() || null,
                anchor_url: contentItem.content.anchor_url?.trim() || null,
                anchor_name_2: contentItem.content.anchor_name_2?.trim() || null,
                anchor_url_2: contentItem.content.anchor_url_2?.trim() || null,
                anchor_name_3: contentItem.content.anchor_name_3?.trim() || null,
                anchor_url_3: contentItem.content.anchor_url_3?.trim() || null,
            });
            
            if (response.data.valid === 'similar') {
                hideLoading();
                addingLink.value = false;
                
                similarityData.value = {
                    similarity: response.data.similarity,
                    expected: response.data.expected,
                    found: response.data.found,
                    url: response.data.url,
                    position: response.data.position || 1
                };
                showSimilarityConfirmation.value = true;
                return;
            } else if (response.data.valid === false) {
                hideLoading();
                addingLink.value = false;
                
                let errorDetail = '';
                if (response.data.errors && Array.isArray(response.data.errors)) {
                    errorDetail = response.data.errors.join('\n');
                } else {
                    errorDetail = response.data.error || $t('Google Docs validation failed');
                }
                
                const wordCountMatch = errorDetail.match(/(\d+)\s+words/);
                const wordCount = wordCountMatch ? parseInt(wordCountMatch[1]) : 0;
                
                openValidationErrorModal(errorDetail, wordCount);
                return;
            }
            
            hideLoading();
            addingLink.value = false;
            proceedWithAddingLink(contentId, link.trim());
            
        } catch (error) {
            hideLoading();
            addingLink.value = false;
            
            if (error.response?.data?.valid === false) {
                let errorDetail = '';
                if (error.response.data.errors && Array.isArray(error.response.data.errors)) {
                    errorDetail = error.response.data.errors.join('\n');
                } else {
                    errorDetail = error.response.data.error || $t('Google Docs validation failed');
                }
                
                const wordCountMatch = errorDetail.match(/(\d+)\s+words/);
                const wordCount = wordCountMatch ? parseInt(wordCountMatch[1]) : 0;
                
                openValidationErrorModal(errorDetail, wordCount);
                return;
            } else {
                toast.add({
                    severity: 'warn',
                    summary: $t('Validation Warning'),
                    detail: $t('Could not validate Google Docs content. Proceeding with adding link.'),
                    life: 3000
                });
                proceedWithAddingLink(contentId, link.trim());
            }
        }
    } else {
        addingLink.value = false;
        proceedWithAddingLink(contentId, link.trim());
    }
};

const proceedWithAddingLink = (contentId, link) => {
    // Find the item that should stay expanded
    const itemToKeepExpanded = props.itemsWithContent.find(item => item.content?.id === contentId);
    
    router.post(
        proxy.route ? proxy.route('order-content.add-link', contentId) : `/admin/order-content/${contentId}/add-link`,
        {
            content_link: link
        },
        {
            preserveScroll: true,
            preserveState: true,
            onStart: () => showLoading($t('Adding Google Docs link...')),
            onSuccess: () => {
                // Ensure item stays expanded
                if (itemToKeepExpanded?.id) {
                    expandedItems.value[itemToKeepExpanded.id] = true;
                    localStorage.setItem(EXPANDED_KEY, JSON.stringify(expandedItems.value));
                }
                
                addingLink.value = false;
                closeAdminLinkModal();
                emit('refresh-needed');
            },
            onFinish: () => {
                hideLoading();
                addingLink.value = false;
            },
            onError: () => {
                hideLoading();
                addingLink.value = false;
            },
        }
    );
};

const clearContentLink = (contentId) => {
    selectedClearContentId.value = contentId;
    showClearLinkModal.value = true;
};

const confirmClearLink = () => {
    const contentId = selectedClearContentId.value;
    
    router.post(
        proxy.route ? proxy.route('order-content.clear-link', contentId) : `/admin/order-content/${contentId}/clear-link`,
        {},
        {
            preserveScroll: true,
            preserveState: true,
            onStart: () => showLoading($t('Clearing Google Docs link...')),
            onFinish: () => hideLoading(),
            onSuccess: () => {
                showClearLinkModal.value = false;
                selectedClearContentId.value = null;
                emit('refresh-needed');
            },
            onError: () => {
                hideLoading();
            }
        }
    );
};

const closeClearLinkModal = () => {
    showClearLinkModal.value = false;
    selectedClearContentId.value = null;
};

const showRejectDialog = (contentId) => {
    selectedRejectContentId.value = contentId;
    rejectReason.value = '';
    showRejectModal.value = true;
};

const closeRejectModal = () => {
    showRejectModal.value = false;
    selectedRejectContentId.value = null;
    rejectReason.value = '';
};

const rejectContent = () => {
    if (!selectedRejectContentId.value) return;

    const contentId = selectedRejectContentId.value;
    const reason = rejectReason.value.trim() || null;
    closeRejectModal();

    router.post(
        proxy.route ? proxy.route('order-content.reject', contentId) : `/admin/order-content/${contentId}/reject`,
        {
            rejection_reason: reason,
            client_message: rejectEmailOptions.value.message,
            email_locale: rejectEmailOptions.value.locale,
        },
        {
            preserveScroll: true,
            preserveState: true,
            onStart: () => showLoading($t('Rejecting content...')),
            onFinish: () => hideLoading(),
            onSuccess: () => emit('refresh-needed'),
            onError: (errors) => {
                toast.add({
                    severity: 'error',
                    summary: $t('Error'),
                    detail: errors.error || $t('Failed to reject content'),
                    life: 5000,
                });
            },
        }
    );

};

const translateAnchorError = (errorCode) => {
    const errorMap = {
        'ANCHOR_NOT_FOUND': $t('Anchor not found in the document'),
        'URL_MISMATCH': $t('Anchor URL does not match'),
        'DOCUMENT_ACCESS_ERROR': $t('Could not access the document'),
        'INVALID_URL': $t('Invalid Google Docs URL')
    };
    return errorMap[errorCode] || $t('Anchor validation failed');
};

const downloadContent = (contentId) => {
    window.open(
        proxy.route ? proxy.route('order-content.download', contentId) : `/admin/order-content/${contentId}/download`,
        '_blank'
    );
};

const openSendToClientApprovalModal = (contentId) => {
    selectedSendApprovalContentId.value = contentId;
    showSendToClientApprovalModal.value = true;
};

const closeSendToClientApprovalModal = () => {
    showSendToClientApprovalModal.value = false;
    selectedSendApprovalContentId.value = null;
    sendingToClientApproval.value = false;
};

const submitSendToClientApproval = (message = null, emailLocale = null) => {
    const contentId = selectedSendApprovalContentId.value;

    router.post(
        proxy.route ? proxy.route('order-content.send-to-client-approval', contentId) : `/admin/order-content/${contentId}/send-to-client-approval`,
        { message, email_locale: emailLocale },
        {
            preserveScroll: true,
            preserveState: true,
            onStart: () => {
                sendingToClientApproval.value = true;
                showLoading($t('Sending to client for approval...'));
            },
            onFinish: () => {
                hideLoading();
                sendingToClientApproval.value = false;
            },
            onSuccess: () => {
                closeSendToClientApprovalModal();
                emit('refresh-needed');
            },
            onError: (errors) => {
                toast.add({
                    severity: 'error',
                    summary: $t('Error'),
                    detail: errors.error || $t('Failed to send for client approval'),
                    life: 5000,
                });
            },
        }
    );
};

const toggleItemExpansion = (itemId) => {
    expandedItems.value[itemId] = !expandedItems.value[itemId];
    localStorage.setItem(EXPANDED_KEY, JSON.stringify(expandedItems.value));
};

const adminApproveContent = (contentId) => {
    selectedForceApproveContentId.value = contentId;
    showForceApproveModal.value = true;
};

const submitForceApprove = (message) => {
    const contentId = selectedForceApproveContentId.value;
    showForceApproveModal.value = false;
    selectedForceApproveContentId.value = null;

    router.post(
        proxy.route ? proxy.route('order-content.admin-approve', contentId) : `/admin/order-content/${contentId}/admin-approve`,
        { message },
        {
            preserveScroll: true,
            preserveState: true,
            onStart: () => showLoading($t('Approving content...')),
            onFinish: () => hideLoading(),
            onSuccess: () => emit('refresh-needed'),
            onError: (errors) => {
                toast.add({
                    severity: 'error',
                    summary: $t('Error'),
                    detail: errors.error || $t('Failed to approve content'),
                    life: 5000,
                });
            },
        }
    );
};

const isContentValid = (content) => {
    if (content.auto_content === 1) {
        return !!(content.content_link && content.client_approved_at);
    }
    return (content.content_link && content.content_link.trim() !== '') || 
           (content.content_link_file && content.content_link_file.trim() !== '');
};
</script>

<template>
    <div :class="$attrs.class">
        <Card :class="{ 'opacity-100': isEnabled, 'opacity-60': !isEnabled }">
            <template #header>
                <div class="p-6 pb-0">
                    <div class="flex items-center gap-3">
                        <div>
                            <h2 class="text-xl font-bold" :class="isEnabled ? 'text-surface-900' : 'text-surface-400'">
                                {{ $t('Content Review') }}
                            </h2>
                            <p class="text-sm font-medium" :class="allConfirmed ? 'text-green-600' : (isEnabled ? 'text-amber-600' : 'text-surface-400')">
                                <span v-if="!isEnabled">{{ $t('Blocked until payment is confirmed') }}</span>
                                <span v-else-if="!hasContent">{{ $t('No content submitted') }}</span>
                                <span v-else-if="allConfirmed">{{ $t('All content confirmed') }}</span>
                                <span v-else>{{ $t('Content pending review') }}</span>
                            </p>
                        </div>
                    </div>
                </div>
            </template>
        <template #content>
            <div v-if="!isEnabled" class="text-center py-12">
                <i class="pi pi-lock text-6xl text-surface-300 mb-4"></i>
                <h3 class="text-xl font-semibold text-surface-500 mb-2">
                    {{ $t('Payment Required') }}
                </h3>
                <p class="text-surface-400">
                    {{ $t('Content review will be enabled after payment confirmation') }}
                </p>
            </div>
            <div v-else-if="!hasContent || !itemsWithContent?.length" class="text-center py-12">
                <i class="pi pi-inbox text-6xl text-surface-300 mb-4"></i>
                <h3 class="text-xl font-semibold text-surface-700 mb-2">
                    {{ $t('No content submitted') }}
                </h3>
                <p class="text-surface-500">
                    {{ $t('Waiting for client to submit content') }}
                </p>
            </div>
            <div v-else class="space-y-4">
                <!-- Content Items -->
                <template v-for="(item, index) in itemsWithContent" :key="item?.id || index">
                    <Card v-if="item && item.content" class="shadow-sm mb-4">
                        <template #header>
                            <div class="p-4 pb-0">
                                <div class="flex items-center justify-between">
                                    <div class="flex items-center gap-3">
                                        <span class="font-bold text-surface-900">{{ index + 1 }}</span>
                                        <div>
                                            <h4 class="font-semibold text-surface-900">{{ item.site_url || 'Site URL not available' }}</h4>
                                            <p class="text-sm text-surface-600">{{ item.type_name || '-' }}</p>
                                        </div>
                                    </div>
                                    <div v-if="item.content" class="flex items-center gap-3">
                                        <div v-if="item.content.confirmed_by_admin_at" class="text-sm text-surface-600">
                                            <span>{{ $t('Confirmed at') }}: <span class="font-medium text-surface-800">{{ new Date(item.content.confirmed_by_admin_at).toLocaleDateString() }}</span></span>
                                        </div>
                                        <Tag v-if="item.content.confirmed_by_admin_at" :value="$t('Confirmed')" severity="success" icon="pi pi-check" />
                                        <Tag v-else-if="item.content.rejected_at" :value="$t('Rejected')" severity="danger" icon="pi pi-times" />
                                        <Tag v-else :value="$t('Pending Review')" severity="warn" icon="pi pi-clock" />
                                        <Tag
                                            v-if="item.content.skipped_validation"
                                            :value="$t('Validation skipped')"
                                            severity="warn"
                                            icon="pi pi-exclamation-triangle"
                                        />

                                        <Button
                                            v-if="item.content?.review_logs?.length"
                                            icon="pi pi-history"
                                            :label="$t('History')"
                                            size="small"
                                            severity="secondary"
                                            text
                                            @click="openHistoryDialog(item.content)"
                                        />

                                        <Button
                                            :icon="expandedItems[item.id] ? 'pi pi-chevron-up' : 'pi pi-chevron-down'"
                                            :label="$t('Show')"
                                            size="small"
                                            severity="secondary"
                                            outlined
                                            @click="toggleItemExpansion(item.id)"
                                        />
                                    </div>
                                </div>
                            </div>
                        </template>
                            <template #content>
                                <div v-show="expandedItems[item.id]">
                                    <SiteFeaturesInfo v-if="item.site_features" :features="item.site_features" /> 
                                <div v-if="item.content" class="space-y-6">
                                    <!-- Anchors Display -->
                                    <div v-if="item.content.anchor_name || item.content.anchor_url || item.content.anchor_name_2 || item.content.anchor_url_2 || item.content.anchor_name_3 || item.content.anchor_url_3" 
                                        class="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                        <h5 class="text-sm font-semibold text-blue-900 mb-3">
                                            <i class="pi pi-link text-blue-700 mr-2"></i>
                                            {{ $t('Anchors') }}
                                        </h5>
                                        <div class="flex flex-col md:flex-row gap-4">
                                            <!-- Anchor 1 -->
                                            <div v-if="item.content.anchor_name || item.content.anchor_url" class="flex-1">
                                                <div class="space-y-2">
                                                    <div>
                                                        <label class="block text-xs font-medium text-blue-800 mb-1">
                                                            {{ $t('Anchor Name') }} 1
                                                        </label>
                                                        <p class="text-sm text-blue-900">{{ item.content.anchor_name || '-' }}</p>
                                                    </div>
                                                    <div>
                                                        <label class="block text-xs font-medium text-blue-800 mb-1">
                                                            {{ $t('Anchor URL') }} 1
                                                        </label>
                                                        <p class="text-xs text-blue-900 break-all">{{ item.content.anchor_url || '-' }}</p>
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            <!-- Divider -->
                                            <Divider v-if="(item.content.anchor_name || item.content.anchor_url) && (item.content.anchor_name_2 || item.content.anchor_url_2)" layout="vertical" class="hidden md:flex" />
                                            
                                            <!-- Anchor 2 -->
                                            <div v-if="item.content.anchor_name_2 || item.content.anchor_url_2" class="flex-1">
                                                <div class="space-y-2">
                                                    <div>
                                                        <label class="block text-xs font-medium text-blue-800 mb-1">
                                                            {{ $t('Anchor Name') }} 2
                                                        </label>
                                                        <p class="text-sm text-blue-900">{{ item.content.anchor_name_2 || '-' }}</p>
                                                    </div>
                                                    <div>
                                                        <label class="block text-xs font-medium text-blue-800 mb-1">
                                                            {{ $t('Anchor URL') }} 2
                                                        </label>
                                                        <p class="text-xs text-blue-900 break-all">{{ item.content.anchor_url_2 || '-' }}</p>
                                                    </div>
                                                </div>
                                            </div>

                                            <!-- Divider -->
                                            <Divider v-if="(item.content.anchor_name_2 || item.content.anchor_url_2) && (item.content.anchor_name_3 || item.content.anchor_url_3)" layout="vertical" class="hidden md:flex" />
                                            
                                            <!-- Anchor 3 -->
                                            <div v-if="item.content.anchor_name_3 || item.content.anchor_url_3" class="flex-1">
                                                <div class="space-y-2">
                                                    <div>
                                                        <label class="block text-xs font-medium text-blue-800 mb-1">
                                                            {{ $t('Anchor Name') }} 3
                                                        </label>
                                                        <p class="text-sm text-blue-900">{{ item.content.anchor_name_3 || '-' }}</p>
                                                    </div>
                                                    <div>
                                                        <label class="block text-xs font-medium text-blue-800 mb-1">
                                                            {{ $t('Anchor URL') }} 3
                                                        </label>
                                                        <p class="text-xs text-blue-900 break-all">{{ item.content.anchor_url_3 || '-' }}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <!-- Language and Submitted At -->
                                <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                                    <div>
                                        <label class="block text-sm font-medium text-surface-700 mb-1">{{ $t('Language') }}</label>
                                        <p class="text-sm text-surface-900">{{ item.content.language || '-' }}</p>
                                    </div>
                                    <div>
                                        <label class="block text-sm font-medium text-surface-700 mb-1">{{ $t('Submitted At') }}</label>
                                        <p class="text-sm text-surface-900">{{ item.content.submited_at ? new Date(item.content.submited_at).toLocaleString() : '-' }}</p>
                                    </div>
                                </div>

                                <!-- Seller & Owner Details -->
                                <RecipientContactBlock
                                    v-if="item.publication_recipient"
                                    :recipient="item.publication_recipient"
                                    :read-only="true"
                                    class="mt-4"
                                />

                                <!-- Content Link or File -->
                                <div v-if="item.content.auto_content === 1" class="p-4 bg-blue-50 rounded-lg border border-blue-200 mt-6">
                                    <label class="block text-sm font-medium text-blue-900 mb-3">
                                        <i class="pi pi-file-word text-blue-600 mr-1"></i>
                                        {{ $t('Article Link (Google Docs)') }}
                                    </label>
                                    <!-- Link exists -->
                                    <div v-if="item.content.content_link" class="flex items-center justify-between gap-4">
                                        <div class="flex items-center gap-2 flex-1 min-w-0">
                                            <i class="pi pi-link text-primary-500 shrink-0"></i>
                                            <a :href="item.content.content_link" target="_blank"
                                               class="text-primary-500 hover:text-primary-600 underline break-all text-sm">
                                                {{ item.content.content_link }}
                                            </a>
                                        </div>
                                        <div class="flex gap-2 shrink-0">
                                            <Button
                                                :label="$t('Edit')"
                                                icon="pi pi-pencil"
                                                size="small"
                                                severity="secondary"
                                                @click="openAdminLinkModal(item.content.id)"
                                                :disabled="!!item.content.client_approved_at || (!!item.content.sent_to_client_approval_at && !item.content.client_rejected_at)"
                                            />
                                            <Button
                                                :label="$t('Clear')"
                                                icon="pi pi-trash"
                                                size="small"
                                                severity="danger"
                                                @click="clearContentLink(item.content.id)"
                                                :disabled="!!item.content.client_approved_at || (!!item.content.sent_to_client_approval_at && !item.content.client_rejected_at)"
                                            />
                                        </div>
                                    </div>
                                    <!-- No link yet -->
                                    <div v-else>
                                        <Button
                                            :label="$t('Add Google Docs Link')"
                                            icon="pi pi-plus"
                                            size="small"
                                            severity="info"
                                            @click="openAdminLinkModal(item.content.id)"
                                        />
                                    </div>
                                    <!-- Client approval status -->
                                    <div v-if="item.content.content_link" class="mt-3 space-y-2">
                                        <div v-if="item.content.client_approved_at" class="p-3 bg-green-50 rounded border border-green-200">
                                            <div class="flex items-center gap-2 text-green-700">
                                                <i class="pi pi-check-circle"></i>
                                                <span class="font-medium text-sm">{{ $t('Client approved the article') }}</span>
                                                <span class="text-xs text-green-500 ml-auto">{{ new Date(item.content.client_approved_at).toLocaleString() }}</span>
                                            </div>
                                        </div>
                                        <div v-else-if="item.content.client_rejected_at" class="p-3 bg-red-50 rounded border border-red-200">
                                            <div class="flex items-center gap-2 text-red-700 mb-1">
                                                <i class="pi pi-times-circle"></i>
                                                <span class="font-medium text-sm">{{ $t('Client rejected the article') }}</span>
                                                <span class="text-xs text-red-500 ml-auto">{{ new Date(item.content.client_rejected_at).toLocaleString() }}</span>
                                            </div>
                                            <p v-if="item.content.client_rejection_reason" class="text-sm text-red-800 mt-1">{{ item.content.client_rejection_reason }}</p>
                                        </div>
                                        <div v-else-if="item.content.sent_to_client_approval_at" class="p-3 bg-amber-50 rounded border border-amber-200">
                                            <div class="flex items-center justify-between text-amber-700">
                                                <div class="flex items-center gap-2">
                                                    <i class="pi pi-clock"></i>
                                                    <span class="font-medium text-sm">{{ $t('Awaiting client approval') }}</span>
                                                    <span class="text-xs text-amber-500 ml-2">{{ $t('Sent') }}: {{ new Date(item.content.sent_to_client_approval_at).toLocaleString() }}</span>
                                                </div>
                                                <Button
                                                    :label="$t('Force Client Approval')"
                                                    icon="pi pi-shield"
                                                    size="small"
                                                    severity="warn"
                                                    @click="adminApproveContent(item.content.id)"
                                                />
                                            </div>
                                        </div>
                                        <!-- Send / Resend button -->
                                        <div v-if="!item.content.client_approved_at && (!item.content.sent_to_client_approval_at || item.content.client_rejected_at)"
                                             class="flex justify-end gap-2">
                                            <Button
                                                :label="$t('Force Client Approval')"
                                                icon="pi pi-shield"
                                                size="small"
                                                severity="warn"
                                                :disabled="!!item.content.rejected_at"
                                                @click="adminApproveContent(item.content.id)"
                                            />
                                            <Button
                                                :label="item.content.client_rejected_at ? $t('Resend to Client') : $t('Send to Client for Approval')"
                                                :icon="item.content.client_rejected_at ? 'pi pi-refresh' : 'pi pi-send'"
                                                size="small"
                                                :severity="item.content.client_rejected_at ? 'warn' : 'info'"
                                                :disabled="!!item.content.rejected_at"
                                                @click="openSendToClientApprovalModal(item.content.id)"
                                            />
                                        </div>
                                    </div>
                                </div>
                                
                                <div v-else-if="item.content.content_link || item.content.content_link_file" class="p-4 bg-surface-50 rounded-lg border border-surface-200 mt-6">
                                    <label class="block text-sm font-medium text-surface-700 mb-2">{{ $t('Content') }}</label>

                                    <div v-if="item.content.content_link" class="flex items-center justify-between">
                                        <div class="flex items-center gap-2">
                                            <i class="pi pi-link text-primary-500"></i>
                                            <a :href="item.content.content_link" target="_blank" class="text-primary-500 hover:text-primary-600 underline break-all">
                                                {{ item.content.content_link }}
                                            </a>
                                        </div>
                                    </div>

                                    <div v-if="item.content.content_link_file" class="flex items-center gap-2">
                                        <i class="pi pi-file text-primary-500"></i>
                                        <Button :label="$t('Download File')" icon="pi pi-download" size="small" @click="downloadContent(item.content.id)" />
                                    </div>

                                </div>
                                <!-- Observations -->
                                <div v-if="item.observations" class="p-4 bg-yellow-50 border border-yellow-200 rounded-lg mt-6">
                                    <label class="block text-sm font-medium text-yellow-800 mb-2 flex items-center gap-2">
                                        <i class="pi pi-exclamation-triangle text-yellow-700"></i>
                                        {{ $t('Observations') }}
                                    </label>
                                    <p class="text-sm text-yellow-900">{{ item.observations }}</p>
                                </div>

                                <!-- Rejection Info -->
                                <div v-if="item.content.rejected_at" class="p-4 bg-red-50 rounded-lg border border-red-200 mt-6">
                                    <div class="flex items-center gap-2 text-red-700 mb-2">
                                        <i class="pi pi-times-circle text-xl"></i>
                                        <div>
                                            <p class="font-medium">{{ $t('Content rejected') }}</p>
                                            <p class="text-sm">{{ new Date(item.content.rejected_at).toLocaleString() }}</p>
                                        </div>
                                    </div>
                                    <div v-if="item.content.rejection_reason">
                                        <label class="block text-sm font-medium text-red-800 mb-1">{{ $t('Rejection reason') }}:</label>
                                        <p class="text-sm text-red-900">{{ item.content.rejection_reason }}</p>
                                    </div>
                                    </div>
                                
                                    <!-- Action Buttons -->
                                    <div v-if="item.content && !item.content.confirmed_by_admin_at && !item.content.rejected_at" class="flex justify-end gap-3 pt-4 border-t border-surface-200">
                                        <Button
                                            :label="$t('Reject')"
                                            icon="pi pi-times"
                                            severity="danger"
                                            outlined
                                            :disabled="!!item.content.sent_to_portal_at"
                                            @click="showRejectDialog(item.content.id)"
                                        />
                                        <Button
                                            :label="$t('Confirm Content')"
                                            icon="pi pi-check"
                                            severity="success"
                                            :disabled="!isContentValid(item.content)"
                                            v-tooltip.top="item.content.auto_content === 1 && !item.content.content_link ? $t('Google Docs link is required') : item.content.auto_content === 1 && !item.content.client_approved_at ? $t('Client approval is required before confirming') : null"
                                            @click="confirmContent(item.content.id)"
                                        />
                                    </div>

                                    <!-- Content Confirmed Success Message -->
                                    <div v-if="item.content && item.content.confirmed_by_admin_at" class="pt-4 border-t border-surface-200">
                                        <div class="p-4 bg-green-50 rounded-lg border border-green-200">
                                            <div class="flex items-center gap-2 text-green-700">
                                                <i class="pi pi-check-circle text-xl"></i>
                                                <div>
                                                    <p class="font-medium">{{ $t('Content confirmed') }}</p>
                                                    <p class="text-sm">{{ new Date(item.content.confirmed_by_admin_at).toLocaleString() }}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </template>
                        </Card>
                    </template>
                </div>
            </template>
        </Card>
    </div>

    <!-- Modals -->

    <!-- Content Rejection Modal -->
    <Dialog
        v-model:visible="showRejectModal"
        modal
        :header="$t('Reject Content')"
        :style="{ width: '500px' }"
        @hide="closeRejectModal"
    >
        <div class="space-y-4">
            <p class="text-surface-700">
                {{ $t('Please provide feedback for the content rejection. This will help the client understand what needs to be revised.') }}
            </p>
            
            <div class="space-y-2">
                <label for="rejectReason" class="block text-sm font-medium text-surface-700">
                    {{ $t('Rejection Reason (Optional)') }}
                </label>
                <Textarea
                    id="rejectReason"
                    v-model="rejectReason"
                    rows="4"
                    class="w-full"
                    :placeholder="$t('Describe what needs to be changed or improved...')"
                />
                <small class="text-surface-600">
                    {{ $t('You can reject content without providing a reason, but feedback helps improve the revision process.') }}
                </small>
            </div>

            <ClientEmailMessagePanel
                email-type="content_rejected"
                :client-locale="order.client?.locale || 'en'"
                @change="payload => rejectEmailOptions = payload"
            />
        </div>
        
        <template #footer>
            <div class="flex justify-end space-x-3">
                <Button
                    :label="$t('Cancel')"
                    icon="pi pi-times"
                    severity="secondary"
                    @click="closeRejectModal"
                    :disabled="rejectingContent"
                />
                <Button
                    :label="$t('Reject Content')"
                    icon="pi pi-times"
                    severity="danger"
                    @click="rejectContent"
                    :loading="rejectingContent"
                />
            </div>
        </template>
    </Dialog>

    <!-- Validation Error Modal -->
    <Dialog v-model:visible="showValidationErrorModal" :style="{ width: '600px' }" :header="$t('Validation Error')" :modal="true">
        <div class="space-y-4">
            <div v-if="validationWordCount > 0" class="p-4 bg-red-50 rounded-lg border border-red-200">
                <div class="flex items-center gap-2 text-red-700">
                    <i class="pi pi-exclamation-triangle text-xl"></i>
                    <p class="font-medium">{{ $t('Word Count Issue') }}: {{ validationWordCount }} {{ $t('words found') }}</p>
                </div>
            </div>
            <div class="p-4 bg-surface-50 rounded-lg">
                <p class="text-surface-900 whitespace-pre-line">{{ validationErrorMessage }}</p>
            </div>
        </div>
        <template #footer>
            <Button :label="$t('Close')" @click="closeValidationErrorModal" />
        </template>
    </Dialog>

    <!-- Similarity Confirmation Modal -->
    <Dialog v-model:visible="showSimilarityConfirmation" :style="{ width: '800px' }" :header="$t('Similarity Detected')" :modal="true">
        <div v-if="similarityData" class="space-y-4">
            <div class="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div class="flex items-center gap-2 text-amber-700">
                    <i class="pi pi-exclamation-triangle text-xl"></i>
                    <p class="font-medium">{{ $t('Similar anchor text found') }} ({{ Math.round(similarityData.similarity) }}% {{ $t('similarity') }})</p>
                </div>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label class="block text-sm font-medium text-surface-700 mb-2">{{ $t('Expected') }}:</label>
                    <div class="p-3 bg-red-50 rounded border" v-html="highlightDifferences(similarityData.expected, similarityData.found).highlighted1"></div>
                </div>
                <div>
                    <label class="block text-sm font-medium text-surface-700 mb-2">{{ $t('Found in document') }}:</label>
                    <div class="p-3 bg-green-50 rounded border" v-html="highlightDifferences(similarityData.expected, similarityData.found).highlighted2"></div>
                </div>
            </div>
            <p class="text-sm text-surface-600">{{ $t('Do you want to proceed with this content confirmation?') }}</p>
        </div>
        <template #footer>
            <Button :label="$t('Cancel')" icon="pi pi-times" text @click="cancelSimilarity" />
            <Button :label="$t('Proceed')" icon="pi pi-check" severity="success" @click="confirmSimilarity" />
        </template>
    </Dialog>

    <!-- Admin Link Modal -->
    <Dialog
        v-model:visible="showAdminLinkModal"
        :header="$t('Add Google Docs Link')"
        :modal="true"
        :closable="true"
        :style="{ width: '50vw' }"
        :breakpoints="{ '960px': '75vw', '640px': '95vw' }"
        @hide="closeAdminLinkModal"
    >
        <div class="space-y-4">
            <div>
                <label for="admin_link" class="block text-sm font-medium text-surface-700 mb-2">
                    {{ $t('Google Docs Link') }} <span class="text-red-500">*</span>
                </label>
                <InputText
                    id="admin_link"
                    v-model="adminLinkInput[selectedContentId]"
                    class="w-full"
                    type="url"
                    :placeholder="$t('Enter Google Docs link for the 500-word article')"
                />
                <small class="text-surface-500 block mt-1">
                    {{ $t('Paste the shareable link from Google Docs containing the 500-word article') }}
                </small>
            </div>
        </div>

        <template #footer>
            <div class="flex justify-end">
                <div class="flex gap-2">
                    <Button
                        :label="$t('Cancel')"
                        severity="secondary"
                        outlined
                        @click="closeAdminLinkModal"
                    />
                    <Button
                        :label="$t('Add Link')"
                        icon="pi pi-check"
                        :loading="addingLink"
                        :disabled="addingLink"
                        @click="submitAdminLink"
                    />
                </div>
            </div>
        </template>
    </Dialog>

    <SendForApprovalModal
        v-model:visible="showSendToClientApprovalModal"
        :loading="sendingToClientApproval"
        :client-locale="order.client?.locale || 'en'"
        @confirm="submitSendToClientApproval"
    />

    <!-- Clear Link Confirmation Modal -->
    <Dialog
        v-model:visible="showClearLinkModal"
        modal
        :header="$t('Clear Google Docs Link')"
        :style="{ width: '450px' }"
        @hide="closeClearLinkModal"
    >
        <div class="flex items-center gap-3 mb-4">
            <i class="pi pi-exclamation-triangle text-amber-500 text-2xl"></i>
            <span class="text-surface-700">{{ $t('Are you sure you want to clear this Google Docs link?') }}</span>
        </div>
        
        <template #footer>
            <div class="flex justify-end space-x-3">
                <Button
                    :label="$t('Cancel')"
                    severity="secondary"
                    @click="closeClearLinkModal"
                />
                <Button
                    :label="$t('Clear')"
                    icon="pi pi-trash"
                    severity="danger"
                    @click="confirmClearLink"
                />
            </div>
        </template>
    </Dialog>

    <ReviewHistoryDialog
        :visible="showHistoryDialog"
        :item="historyItem"
        @hide="closeHistoryDialog"
    />

    <AdminForceApproveModal
        :visible="showForceApproveModal"
        @update:visible="showForceApproveModal = $event"
        @confirm="submitForceApprove"
    />
</template>